import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useEMTState } from '@/store'
import {
  EmtAccordion,
  EmtButton,
  EmtButtonGroup,
  EmtCheckbox,
  EmtDistance,
  EmtIcon,
  EmtSeparator,
  EmtTextField,
  EmtTypography,
  useAccordion,
  useGenderButton,
} from 'emt-components-v2'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import { useDisplayDistance, useIsQueryOwner } from '@/hooks'
import { styled, SxProps } from '@mui/material/styles'
import useEnterKey from '@/hooks/useEnterKey'
import {
  generateDefaultFiltersValues,
  SearchFiltersKeys,
  SearchFiltersValues,
  SearchType,
} from '@/modules/Search/apiTypes/base'
import { Gender } from '@/dataV3/Common/Gender'

type Props = React.PropsWithChildren<{
  defaultFiltersValues: SearchFiltersValues
  shortFilter: SearchType
  isLoading?: boolean
  onFilterChange: (filtersValues: SearchFiltersValues) => void
  manualRequest?: boolean
  onEraseFilter: (filtersValues?: SearchFiltersValues) => void
  onSearchButtonClick: (filtersValue?: SearchFiltersValues) => void
  onClose: () => void
  disabledFilters?: Array<SearchFiltersKeys>
}>

const accordionCheckboxContainerStyle: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'row-reverse',
  marginRight: 0,
}

const ActionButtonsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '19px',
  padding: '15px',
}))

const distanceCorrespondingValues = {
  percentToValue: {
    0: 0.5,
    10: 1,
    20: 2,
    30: 3,
    40: 4,
    50: 5,
    60: 10,
    70: 15,
    80: 20,
    90: 50,
    100: 50,
  },
  valueToPercent: {
    0.5: 0.5,
    1: 10,
    2: 20,
    3: 30,
    4: 40,
    5: 50,
    10: 60,
    15: 70,
    20: 80,
    50: 90,
    100: 100,
  },
}

const Filters: React.FC<Props> = ({
  shortFilter,
  onFilterChange,
  defaultFiltersValues = generateDefaultFiltersValues(),
  isLoading = false,
  manualRequest = false,
  onEraseFilter,
  onSearchButtonClick,
  onClose,
  disabledFilters = [],
}) => {
  const { t } = useTranslation('search')
  const [, type] = useIsQueryOwner()
  const [enterKeyRefClick] = useEnterKey()

  const { dogSizes, disponibilityChoices, dogAffinityChoices } = useEMTState().configuration
  const displayDistance = useDisplayDistance()

  const availability = disponibilityChoices.find((disponibilityChoice) => disponibilityChoice.slug === 'disponibility')
  const entrust = disponibilityChoices.find((disponibilityChoice) => disponibilityChoice.slug === 'attention')
  const affinities = dogAffinityChoices.find((disponibilityChoice) => disponibilityChoice.slug === 'affinities')

  const [accordionState, handleAccordionClick] = useAccordion({
    name: true,
    date: true,
    sizes: true,
    distance: true,
    hug: true,
    availability: true,
    gender: true,
    affinities: true,
  })
  const [filtersValues, setFiltersValues] = useState<SearchFiltersValues>({
    ...generateDefaultFiltersValues(),
    ...defaultFiltersValues,
  })

  const [activeGenderButton, setActiveGenderButton] = useGenderButton('all')

  const internalSetFiltersValues = (filterKey: SearchFiltersKeys, value) => {
    setFiltersValues((oldValues) => {
      const newValues = { ...oldValues, [filterKey]: value }
      if (value === undefined) delete newValues[filterKey]

      if (!manualRequest) onFilterChange(newValues)
      return newValues
    })
  }

  const handleFiltersValuesChange = (filterKey: SearchFiltersKeys, value) => internalSetFiltersValues(filterKey, value)

  const handleDogSizesFilterChange = (value, checked: boolean) => {
    const selectedDogSizes = checked
      ? [...(filtersValues.size || []), value]
      : filtersValues.size?.filter((size) => size !== value)
    internalSetFiltersValues('size', selectedDogSizes)
  }

  const handleResetFilters = () => {
    const defaultValues = generateDefaultFiltersValues()
    setFiltersValues(defaultValues)
    if (!manualRequest) onFilterChange(defaultValues)

    if (onEraseFilter) {
      onEraseFilter(defaultValues)
    }
  }

  const handleDistanceFilterChange = (value: string) => {
    internalSetFiltersValues('distance', parseFloat(value))
  }

  const handleDogSexFilterChange = (value: 'all' | Gender) => {
    setActiveGenderButton(value)
    internalSetFiltersValues('gender', value === 'all' ? undefined : value)
  }

  useEffect(() => {
    setActiveGenderButton(defaultFiltersValues.gender ? defaultFiltersValues.gender : 'all')
  }, [])

  useEffect(() => {
    handleAccordionClick(shortFilter, true)
  }, [shortFilter])

  const isIOS =
    typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream

  return (
    <>
      {/* HEADER */}
      <Box sx={{ paddingLeft: '10px', paddingRight: '30px' }} className={'EmtReactNativeWebViewMarginTop'}>
        <Box sx={{ display: 'flex', padding: '20px 0', justifyContent: 'space-between', alignItems: 'center' }}>
          <EmtTypography variant="h4" fontWeight={800}>
            {t('filters.title')}
          </EmtTypography>
          <EmtIcon className="emt-x-1" onClick={onClose} sx={{ fontSize: '18px', cursor: 'pointer' }} />
        </Box>
        <EmtSeparator />
      </Box>
      {/* END HEADER */}
      <Box
        sx={{
          minWidth: 340,
          overflowX: 'hidden',
          position: 'relative',
          scrollbarColor: '#D2D1D4 transparent',
          paddingRight: '30px',
          paddingLeft: '10px',
          paddingTop: '10px',
          paddingBottom: '80px',

          '&::-webkit-scrollbar': {
            width: '5px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#D2D1D4',
            borderRadius: '8px',
          },
        }}
      >
        <Box className={'EmtReactNativeWebViewPaddingBottom'}>
          {/* Search by name */}
          <EmtAccordion
            title={t(`filters.searchByName.${type}.title`)}
            expanded={accordionState.name}
            onClick={() => handleAccordionClick('name')}
          >
            <EmtTextField
              label={t(`filters.searchByName.${type}.fieldLabel`)}
              type="search"
              fullWidth
              value={filtersValues.name}
              onChange={(e) =>
                e.target.value.length > 2 || e.target.value === ''
                  ? handleFiltersValuesChange('name', e.target.value)
                  : null
              }
            />
          </EmtAccordion>
          {/* END Search by name */}

          {/* Sizes */}
          {type === 'borrower' && (
            <EmtAccordion
              title={t('filters.dogSizes.title')}
              expanded={accordionState.sizes}
              onClick={() => handleAccordionClick('sizes')}
            >
              {dogSizes
                .sort((a, b) => a.position - b.position)
                .map((dogSize, key) => (
                  <FormControlLabel
                    key={key}
                    disabled={!manualRequest && isLoading}
                    control={
                      <EmtCheckbox
                        onChange={(e) => handleDogSizesFilterChange(dogSize.id, e.target.checked)}
                        checked={filtersValues.size?.includes(dogSize.id)}
                        controlledCheckbox={false}
                      />
                    }
                    label={
                      <EmtTypography variant="body2" lineHeight={1} color="greyGradient.main">
                        {dogSize.name} <EmtTypography variant="caption">{dogSize.description}</EmtTypography>
                      </EmtTypography>
                    }
                    sx={accordionCheckboxContainerStyle}
                  />
                ))}
            </EmtAccordion>
          )}
          {/* END Sizes */}

          {/* Distance */}
          {!disabledFilters.includes('distance') ? (
            <EmtAccordion
              title={t('filters.distance.title')}
              expanded={accordionState.distance}
              onClick={() => handleAccordionClick('distance')}
            >
              <Box sx={{ padding: '30px 15px', paddingBottom: '15px' }}>
                {/* <EmtDistance
            min={0}
            max={50}
            value={[0, filtersValues.distance ? Number(filtersValues.distance) : 5]}
            defaultValue={[
              defaultFiltersValues.distanceMin ? Number(defaultFiltersValues.distanceMin) : 0,
              defaultFiltersValues.distance ? Number(defaultFiltersValues.distance) : 5,
            ]}
            onChangeCommitted={(e, values: number[]) => handleDistanceFilterChange(values)}
          /> */}

                <EmtDistance
                  disabled={!manualRequest && isLoading}
                  min={0}
                  max={100}
                  defaultValue={
                    defaultFiltersValues.distance
                      ? distanceCorrespondingValues.valueToPercent[defaultFiltersValues.distance]
                      : 0
                  }
                  onChange={(v: number, _event: Event) => {
                    // _event is the event object, value is the slider value
                    if (isIOS && _event?.type === 'mousedown') {
                      return
                    }
                    handleDistanceFilterChange(distanceCorrespondingValues.percentToValue[v])
                  }}
                  marks={[
                    {
                      value: 0,
                      label: displayDistance(0.5),
                    },
                    {
                      value: 10,
                    },
                    {
                      value: 20,
                    },
                    {
                      value: 30,
                    },
                    {
                      value: 40,
                    },
                    {
                      value: 50,
                    },
                    {
                      value: 60,
                    },
                    {
                      value: 70,
                    },
                    {
                      value: 80,
                    },
                    {
                      value: 100,
                      label: displayDistance(50),
                    },
                  ]}
                  valueLabelFormat={(v: number) => {
                    return displayDistance(
                      distanceCorrespondingValues.percentToValue[v] || distanceCorrespondingValues.valueToPercent[v],
                      '',
                      1,
                    )
                  }}
                  step={10}
                />
              </Box>
            </EmtAccordion>
          ) : null}

          {/* END Distance */}

          {/* DogSex */}
          {type === 'borrower' && (
            <EmtAccordion
              title={t('filters.dogSex.title')}
              expanded={accordionState.gender}
              onClick={() => handleAccordionClick('gender')}
            >
              <EmtButtonGroup>
                <EmtButton
                  fullWidth
                  color={activeGenderButton === 'all' ? 'primary' : 'white'}
                  onClick={() => handleDogSexFilterChange('all')}
                >
                  {t('filters.dogSex.all')}
                </EmtButton>
                <EmtButton
                  fullWidth
                  color={activeGenderButton === Gender.male ? 'primary' : 'white'}
                  onClick={() => handleDogSexFilterChange(Gender.male)}
                >
                  {t('filters.dogSex.male')}
                </EmtButton>
                <EmtButton
                  fullWidth
                  color={activeGenderButton === Gender.female ? 'primary' : 'white'}
                  onClick={() => handleDogSexFilterChange(Gender.female)}
                >
                  {t('filters.dogSex.female')}
                </EmtButton>
              </EmtButtonGroup>
            </EmtAccordion>
          )}
          {/* END DogSex */}

          {/* Entrust */}
          <EmtAccordion
            title={t('filters.entrust.title')}
            expanded={accordionState.hug}
            onClick={() => handleAccordionClick('hug')}
          >
            {entrust?.types.map((eType) => (
              <FormControlLabel
                disabled={!manualRequest && isLoading}
                key={eType.slug}
                control={
                  <EmtCheckbox
                    checked={filtersValues[eType.slug]}
                    onChange={(e) => handleFiltersValuesChange(eType.slug as SearchFiltersKeys, e.target.checked)}
                    controlledCheckbox={false}
                  />
                }
                label={
                  <EmtTypography variant="body2" lineHeight={1} color="greyGradient.main">
                    {eType.sentence[type === 'dogowner' ? 'ownerSentence' : 'borrowerSentence'].profileSentence}
                  </EmtTypography>
                }
                sx={accordionCheckboxContainerStyle}
              />
            )) || <Fragment />}
          </EmtAccordion>
          {/* END Entrust */}

          {/* Availability */}
          <EmtAccordion
            title={t(`filters.availability.${type}.title`)}
            expanded={accordionState.availability}
            onClick={() => handleAccordionClick('availability')}
          >
            {availability?.types.map((dType) => (
              <FormControlLabel
                disabled={!manualRequest && isLoading}
                key={dType.slug}
                control={
                  <EmtCheckbox
                    checked={filtersValues[dType.slug]}
                    onChange={(e) => handleFiltersValuesChange(dType.slug as SearchFiltersKeys, e.target.checked)}
                    controlledCheckbox={false}
                  />
                }
                label={
                  <EmtTypography variant="body2" lineHeight={1} color="greyGradient.main">
                    {dType.sentence[type === 'dogowner' ? 'ownerSentence' : 'borrowerSentence'].profileSentence}
                  </EmtTypography>
                }
                sx={accordionCheckboxContainerStyle}
              />
            )) || <Fragment />}
          </EmtAccordion>
          {/* END Availability */}

          {/* Affinities */}
          {type === 'borrower' && (
            <EmtAccordion
              title={t('filters.affinities.title')}
              expanded={accordionState.affinities}
              onClick={() => handleAccordionClick('affinities')}
            >
              {affinities?.types.map((aType) => (
                <FormControlLabel
                  disabled={!manualRequest && isLoading}
                  key={aType.slug}
                  control={
                    <EmtCheckbox
                      checked={filtersValues[aType.slug]}
                      onChange={(e) => handleFiltersValuesChange(aType.slug as SearchFiltersKeys, e.target.checked)}
                      controlledCheckbox={false}
                    />
                  }
                  label={
                    <EmtTypography variant="body2" lineHeight={1} color="greyGradient.main">
                      {t(`filters.affinities.${aType.slug}`)}
                    </EmtTypography>
                  }
                  sx={accordionCheckboxContainerStyle}
                />
              )) || <Fragment />}
            </EmtAccordion>
          )}
          {/* END Affinities */}
        </Box>
      </Box>
      {/* BUTTONS */}
      <Box
        sx={{
          position: 'absolute',
          zIndex: 2,
          width: '100%',
          background: '#fff',
          bottom: 0,
          left: 0,
          boxShadow: '0px 12px 16px rgba(0, 0, 0, 0.08), 0px 4px 56px rgba(0, 0, 0, 0.08)',
        }}
        className={'EmtReactNativeWebViewPaddingBottom'}
      >
        <ActionButtonsWrapper>
          <Box sx={{ width: '30%' }}>
            <EmtButton size="large" variant="outlined" onClick={handleResetFilters} fullWidth>
              {t('filters.buttons.erase')}
            </EmtButton>
          </Box>
          <Box sx={{ width: '70%' }}>
            <EmtButton
              ref={enterKeyRefClick}
              size="large"
              startIcon={<EmtIcon className="emt-search-1" />}
              onClick={() => (manualRequest ? onSearchButtonClick(filtersValues) : onSearchButtonClick())}
              fullWidth
            >
              {t('filters.buttons.search')}
            </EmtButton>
          </Box>
        </ActionButtonsWrapper>
      </Box>
      {/* END BUTTONS */}
    </>
  )
}

export default Filters
